<template>
  <header class="desktop-header" role="banner">
    <div class="desktop-header__inner">
      <div class="desktop-header__logo-group">
        <router-link :to="{ name: 'duties' }" class="desktop-header__logo" aria-label="Coridor">
          <img src="@/assets/landing-page-images/logo.webp" alt="Coridor" />
        </router-link>
        <template v-if="brandLogo">
          <div class="desktop-header__logo-divider" aria-hidden="true"></div>
          <img class="desktop-header__brand-logo" :src="brandLogo" :alt="brandName" />
        </template>
      </div>

      <nav class="desktop-header__nav" aria-label="Primary">
        <router-link :to="{ name: 'home-map' }" class="desktop-header__link">
          {{ $t('menu.map') }}
        </router-link>
        <router-link :to="{ name: 'duties' }" class="desktop-header__link">
          {{ $t('menu.missions') }}
        </router-link>
        <router-link :to="{ name: 'events' }" class="desktop-header__link">
          {{ $t('menu.events') }}
        </router-link>
        <router-link :to="{ name: 'achievements' }" class="desktop-header__link">
          {{ $t('menu.rewards') }}
        </router-link>
        <router-link :to="{ name: 'profile' }" class="desktop-header__link">
          {{ $t('menu.profile') }}
        </router-link>
      </nav>

      <div class="desktop-header__actions">
        <LanguageSwitcher />

        <div class="dh-menu-wrapper" ref="triggerRef">
          <button
            type="button"
            class="desktop-header__icon-button"
            :aria-label="$t('duties.settings.title')"
            :title="$t('duties.settings.title')"
            @click.stop="toggleMenu"
          >
            <Settings :size="18" />
          </button>

          <transition name="dh-menu-fade">
            <div v-if="menuOpen" ref="panelRef" class="dh-menu">
              <button type="button" class="dh-menu__item" @click="openCookiePreferences">
                <Cookie :size="16" />
                <span>{{ $t('cookieConsent.cookieSettings') }}</span>
              </button>
              <div class="dh-menu__divider"></div>
              <button type="button" class="dh-menu__item dh-menu__item--danger" @click="logout">
                <LogOut :size="16" />
                <span>{{ $t('common.logout') }}</span>
              </button>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { defineComponent, onMounted, onBeforeUnmount, ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import { LogOut, Settings, Cookie } from '@lucide/vue'
import { useConsent } from '@/composables/useConsent'

export default defineComponent({
  name: 'DesktopHeader',
  components: { LanguageSwitcher, LogOut, Settings, Cookie },
  setup () {
    const router = useRouter()
    const brandLogo = ref(null)
    const brandName = ref('')
    const menuOpen = ref(false)
    const triggerRef = ref(null)
    const panelRef = ref(null)
    const { openPreferencesModal } = useConsent()

    onMounted(async () => {
      try {
        const res = await axios.get('/brand-context')
        if (!res.data?.isDefaultBrand && res.data?.logo?.fullPath) {
          brandLogo.value = res.data.logo.fullPath
          brandName.value = res.data.name || ''
        }
      } catch { }

      document.addEventListener('mousedown', handleOutsideClick)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('mousedown', handleOutsideClick)
    })

    const toggleMenu = () => { menuOpen.value = !menuOpen.value }
    const closeMenu = () => { menuOpen.value = false }

    const handleOutsideClick = (e) => {
      if (!menuOpen.value) return
      const trigger = triggerRef.value
      const panel = panelRef.value
      if ((trigger && trigger.contains(e.target)) || (panel && panel.contains(e.target))) return
      closeMenu()
    }

    const openCookiePreferences = () => {
      closeMenu()
      openPreferencesModal()
    }

    const logout = () => {
      closeMenu()
      router.push('/logout')
    }

    return { brandLogo, brandName, menuOpen, triggerRef, panelRef, toggleMenu, openCookiePreferences, logout }
  }
})
</script>

<style scoped lang="scss">
.desktop-header__logo-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.desktop-header__logo-divider {
  width: 1px;
  height: 28px;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 1px;
  flex-shrink: 0;
}

.desktop-header__brand-logo {
  display: block;
  height: 28px;
  width: auto;
  max-width: 120px;
  object-fit: contain;
}

.desktop-header__icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid rgba(114, 111, 111, 0.22);
  border-radius: 50%;
  background: #ffffff;
  color: #726f6f;
  transition: border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease;
}

.desktop-header__icon-button:hover {
  border-color: rgba(114, 111, 111, 0.38);
  color: var(--brand-color);
}

// ─── Dropdown menu ─────────────────────────────────────────────────────────────

.dh-menu-wrapper {
  position: relative;
}

.dh-menu {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  min-width: 200px;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  padding: 6px;
  z-index: 1050;
}

.dh-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;

  &:hover {
    background: #f3f4f6;
  }

  &--danger {
    color: var(--brand-color);

    &:hover {
      background: rgba(var(--brand-color-rgb), 0.08);
    }
  }
}

.dh-menu__divider {
  height: 1px;
  background: #f3f4f6;
  margin: 4px 0;
}

// ─── Animation ─────────────────────────────────────────────────────────────────

.dh-menu-fade-enter-active,
.dh-menu-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dh-menu-fade-enter-from,
.dh-menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
